// src/Layout/SidebarLayout.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronDown,
  Menu,
  X,
  LogOut,
  Sun,
  Moon,
  Layout,
  UserCircle,
  Group,
  UserPlus,
  Combine,
  MapPinned,
  Backpack,
  Minimize,
  Maximize,
} from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { usePermissions } from "@/context/PermissionContext";
import { routePermissions } from "@/constants/routePermissions";
import mawgiflogo from "@/assets/img/mawgiflogo.png";
import { clearSession } from "@/utils/authSession";

// Pages that always render in light mode; the user's selected theme is restored on leaving them.
const FORCED_LIGHT_ROUTES = ["/", "/board-pack", "/board-pack-config"];
// Must match the ThemeProvider storageKey in main.jsx.
const THEME_STORAGE_KEY = "vite-ui-theme";

// NOTE: FloatingChatbot is NO LONGER imported here.
// It lives in ProtectedRoute so its state persists across route changes.

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const Collapse = ({ isOpen, children }) => (
  <AnimatePresence initial={false}>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

const HoverFlyout = ({ enabled, item, onNavigate, children }) => {
  const wrapperRef = useRef(null);
  const closeTimer = useRef(null);
  const [rect, setRect] = useState(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!enabled) setRect(null);
  }, [enabled]);

  if (!enabled) return children;

  const open = () => {
    clearTimeout(closeTimer.current);
    if (wrapperRef.current)
      setRect(wrapperRef.current.getBoundingClientRect());
  };

  const keepOpen = () => clearTimeout(closeTimer.current);

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setRect(null), 120);
  };

  const handleNavigate = (name) => {
    clearTimeout(closeTimer.current);
    setRect(null);
    onNavigate(name);
  };

  const linkClass = ({ isActive }) =>
    `block px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
      isActive
        ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
        : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/40"
    }`;

  return (
    <div ref={wrapperRef} onMouseEnter={open} onMouseLeave={scheduleClose}>
      {children}
      {createPortal(
        <AnimatePresence>
          {rect && (
            <motion.div
              key="collapsed-flyout"
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.14 }}
              onMouseEnter={keepOpen}
              onMouseLeave={scheduleClose}
              className="pl-2"
              style={{
                position: "fixed",
                left: rect.right + 8,
                top: rect.top,
                zIndex: 2000,
                maxHeight: window.innerHeight - rect.top - 12,
                overflowY: "auto",
              }}
            >
              {item.hasSubmenu ? (
                <div className="min-w-[190px] bg-sidebar border border-sidebar-border rounded-xl shadow-2xl p-1.5">
                  <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/50">
                    {item.name.trim()}
                  </p>
                  {item.submenu.map((subItem, idx) =>
                    subItem.hasSubmenu ? (
                      <div key={idx}>
                        <p className="px-3 pt-2 pb-1 text-[11px] font-medium text-sidebar-foreground/50">
                          {subItem.name}
                        </p>
                        {subItem.submenu.map((nestedItem, nidx) => (
                          <NavLink
                            key={nidx}
                            to={nestedItem.path}
                            onClick={() => handleNavigate(nestedItem.name)}
                            className={linkClass}
                          >
                            {nestedItem.name}
                          </NavLink>
                        ))}
                      </div>
                    ) : (
                      <NavLink
                        key={idx}
                        to={subItem.path}
                        onClick={() => handleNavigate(subItem.name)}
                        className={linkClass}
                      >
                        {subItem.name}
                      </NavLink>
                    )
                  )}
                </div>
              ) : (
                <div className="px-3 py-2 text-sm font-medium whitespace-nowrap bg-sidebar text-sidebar-foreground border border-sidebar-border rounded-lg shadow-xl">
                  {item.name.trim()}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

const navItems = [
  { name: " Dashboard", path: "/", icon: Group },
  {
    name: "Cube Management",
    path: "",
    icon: Combine,
    hasSubmenu: true,
    submenu: [
      { name: "Instance", path: "/instance" },
      { name: "Cube", path: "/cube" },
      { name: "Mapping", path: "/mapping" },
    ],
  },
  {
    name: "Board Pack Management",
    path: "",
    icon: Backpack,
    hasSubmenu: true,
    submenu: [
      { name: "Configuration", path: "/board-pack-config" },
      { name: "Board Pack", path: "/board-pack" },
      { name: "Finalized Board Pack", path: "/finalized-boardpacks" },
    ],
  },
  {
    name: " Map Overview",
    path: "",
    icon: MapPinned,
    hasSubmenu: true,
    submenu: [
      {
        name: "On Street",
        hasSubmenu: true,
        submenu: [
          { name: "Overview", path: "/on-street-overview" },
          { name: "Master Data", path: "/on-street-master-data" },
        ],
      },
      {
        name: "Off Street",
        hasSubmenu: true,
        submenu: [
          { name: "Overview", path: "/off-street-overview" },
          { name: "Master Data", path: "/off-street-master-data" },
        ],
      },
    ],
  },
  {
    name: "Users & Roles",
    path: "",
    icon: UserPlus,
    hasSubmenu: true,
    submenu: [
      { name: "Users", path: "/user" },
      { name: "Roles", path: "/role" },
      { name: "Permissions", path: "/permission" },
      { name: " Access Control", path: "/access-control" },
    ],
  },
];

const ProfileDropdown = ({
  user,
  theme,
  setTheme,
  isHorizontal,
  onProfileClick,
  onToggleLayout,
  onLogout,
  hideThemeToggle,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const menuRef = useRef(null);

  useEffect(() => {
    if (onOpenChange) onOpenChange(isOpen);
  }, [isOpen]);

  useEffect(() => {
  const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
  document.addEventListener("fullscreenchange", handleChange);
  return () => document.removeEventListener("fullscreenchange", handleChange);
}, []);

const toggleFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  } catch (err) {
    console.error("Fullscreen error:", err);
  }
};

  useClickOutside(menuRef, () => {
    if (isOpen) setIsOpen(false);
  });

  const close = () => setIsOpen(false);

  return (
    <div className="relative z-[99999]" ref={menuRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`flex items-center gap-2 p-1 pr-2 rounded-full transition-colors ${
          isOpen ? "bg-sidebar-accent" : "hover:bg-sidebar-accent"
        }`}
        title={`Profile — ${user?.full_name || "User"}`}
      >
        <div className="w-8 h-8 rounded-full bg-sidebar-primary flex items-center justify-center text-sidebar-primary-foreground font-bold text-xs shadow-sm flex-shrink-0">
          {user?.full_name?.charAt(0)?.toUpperCase() || "D"}
        </div>
        <span className="hidden sm:block text-sm font-medium text-sidebar-foreground max-w-[120px] truncate">
          {user?.full_name || "Demo User"}
        </span>
        <ChevronDown
          size={14}
          className={`text-sidebar-foreground/70 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-72 bg-sidebar border border-sidebar-border rounded-xl shadow-2xl overflow-hidden z-[99999]"
          >
            <div className="p-4 border-b border-sidebar-border bg-sidebar-accent/30">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-sidebar-primary flex items-center justify-center text-sidebar-primary-foreground font-bold text-sm shadow-sm flex-shrink-0">
                  {user?.full_name?.charAt(0)?.toUpperCase() || "D"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-sidebar-foreground truncate">
                    {user?.full_name || "Demo User"}
                  </p>
                  <p className="text-xs text-sidebar-foreground/60 truncate">
                    {user?.email || user?.role_name || "Administrator"}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-1.5">
              <button
                onClick={() => {
                  close();
                  onProfileClick();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground rounded-lg transition-colors"
              >
                <UserCircle className="w-4 h-4 flex-shrink-0" />
                <span>My Profile</span>
              </button>

              {!hideThemeToggle && (
                <button
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground rounded-lg transition-colors"
                >
                  {theme === "dark" ? (
                    <Sun className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <Moon className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span className="flex-1 text-left">
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </span>
                </button>
              )}

              <button
                onClick={() => {
                  close();
                  onToggleLayout();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground rounded-lg transition-colors"
              >
                <Layout className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left">
                  {isHorizontal ? "Switch to Vertical" : "Switch to Horizontal"}
                </span>
              </button>
              {document.fullscreenEnabled && (
  <button
    onClick={() => {
      close();
      toggleFullscreen();
    }}
    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground rounded-lg transition-colors"
  >
    {isFullscreen ? (
      <Minimize className="w-4 h-4 flex-shrink-0" />
    ) : (
      <Maximize className="w-4 h-4 flex-shrink-0" />
    )}
    <span className="flex-1 text-left">
      {isFullscreen ? "Exit Full Screen" : "Full Screen"}
    </span>
  </button>
)}
            </div>

            <div className="p-1.5 border-t border-sidebar-border">
              <button
                onClick={() => {
                  close();
                  onLogout();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span>Logout</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SidebarLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { hasPermission } = usePermissions();
  const user = JSON.parse(localStorage.getItem("user"));

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [openNestedSubMenu, setOpenNestedSubMenu] = useState(null);
  const [isHorizontal, setIsHorizontal] = useState(() => {
    const saved = localStorage.getItem("sidebarLayout");
    return saved ? saved === "horizontal" : true;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 1024
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const sidebarRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const desktopNavRef = useRef(null);
  const hamburgerRef = useRef(null);

  useEffect(() => {
    if (!location.pathname.startsWith("/off-street-overview")) {
      try {
        sessionStorage.removeItem("offstreet_sync_v1");
      } catch {
        // ignore
      }
    }
  }, [location.pathname]);

  const filteredNavItems = navItems
    .map((item) => {
      if (item.hasSubmenu) {
        const filteredSubmenu = item.submenu
          .map((sub) => {
            if (sub.hasSubmenu) {
              const filteredNested = sub.submenu.filter((nested) => {
                const requiredPermission = routePermissions[nested.path];
                return !requiredPermission || hasPermission(requiredPermission);
              });
              return { ...sub, submenu: filteredNested };
            }
            const requiredPermission = routePermissions[sub.path];
            return !requiredPermission || hasPermission(requiredPermission)
              ? sub
              : null;
          })
          .filter((sub) => {
            if (!sub) return false;
            if (sub.hasSubmenu && sub.submenu.length === 0) return false;
            return true;
          });
        return { ...item, submenu: filteredSubmenu };
      }
      const requiredPermission = routePermissions[item.path];
      return !requiredPermission || hasPermission(requiredPermission)
        ? item
        : null;
    })
    .filter((item) => {
      if (!item) return false;
      if (item.hasSubmenu && item.submenu.length === 0) return false;
      return true;
    });

  const isSubmenuActive = (submenu) =>
    submenu.some((sub) =>
      sub.hasSubmenu
        ? sub.submenu.some((nested) => location.pathname.startsWith(nested.path))
        : location.pathname.startsWith(sub.path)
    );

  useClickOutside(sidebarRef, () => {
    if ((isMobile || isTablet) && isSidebarOpen && !isHorizontal) {
      setIsSidebarOpen(false);
      setOpenSubMenu(null);
    }
  });

  useClickOutside(mobileMenuRef, (e) => {
    if (hamburgerRef.current?.contains(e.target)) return;
    if (isMobileMenuOpen && isHorizontal) {
      setIsMobileMenuOpen(false);
      setOpenSubMenu(null);
    }
  });

  useClickOutside(desktopNavRef, () => {
    if (isHorizontal && openSubMenu && !isMobileMenuOpen) {
      setOpenSubMenu(null);
      setOpenNestedSubMenu(null);
    }
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!mobile && !tablet && isMobileMenuOpen) setIsMobileMenuOpen(false);
      if (!mobile && !tablet && !isHorizontal) setIsSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen, isHorizontal]);

  useEffect(() => {
    const activeSubMenu = filteredNavItems.find(
      (item) => item.hasSubmenu && isSubmenuActive(item.submenu)
    );
    if (!isHorizontal && !isCollapsed) {
      setOpenSubMenu(activeSubMenu ? activeSubMenu.name : null);
      if (activeSubMenu) {
        const activeNested = activeSubMenu.submenu.find(
          (sub) =>
            sub.hasSubmenu &&
            sub.submenu.some((nested) =>
              location.pathname.startsWith(nested.path)
            )
        );
        setOpenNestedSubMenu(activeNested ? activeNested.name : null);
      } else {
        setOpenNestedSubMenu(null);
      }
    }
  }, [location.pathname, isHorizontal, isCollapsed]);

  useEffect(() => {
    localStorage.setItem(
      "sidebarLayout",
      isHorizontal ? "horizontal" : "vertical"
    );
  }, [isHorizontal]);

  const isForcedLightPage = FORCED_LIGHT_ROUTES.includes(location.pathname);
  // Theme the user had selected before entering a forced-light page (null when not on one).
  const preForcedThemeRef = useRef(null);

  useEffect(() => {
    if (isForcedLightPage) {
      if (preForcedThemeRef.current !== null) return;
      preForcedThemeRef.current = theme;
      if (theme !== "light") {
        setTheme("light");
        // setTheme persists to localStorage; keep the user's real choice stored instead.
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      }
    } else if (preForcedThemeRef.current !== null) {
      const previousTheme = preForcedThemeRef.current;
      preForcedThemeRef.current = null;
      if (previousTheme !== theme) setTheme(previousTheme);
    }
    // Only react to entering/leaving forced-light pages; theme/setTheme are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isForcedLightPage]);

  const thisPages = [
    "/user",
    "/role",
    "/permission",
    "/instance",
    "/on-street-master-data",
    "/off-street-master-data",
    "/cube",
    "/on-street-overview",
    "/off-street-overview",
    "/access-control",
    "/profile"
  ];

  const toggleSidebar = () => {
    if (isMobile || isTablet) {
      setIsSidebarOpen(!isSidebarOpen);
      if (!isSidebarOpen) setOpenSubMenu(null);
    } else {
      setIsCollapsed(!isCollapsed);
      if (!isCollapsed) setOpenSubMenu(null);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenSubMenu(null);
  };

  const toggleLayoutMode = () => {
    setIsHorizontal(!isHorizontal);
    setOpenSubMenu(null);
    setIsMobileMenuOpen(false);
    setIsCollapsed(false);
    if (!isHorizontal && (isMobile || isTablet)) setIsSidebarOpen(true);
    else if (isHorizontal && (isMobile || isTablet)) setIsSidebarOpen(false);
  };

  const handleItemClick = (itemName, hasSubmenu) => {
    if (hasSubmenu && !isCollapsed) {
      setOpenSubMenu((prev) => (prev === itemName ? null : itemName));
    } else {
      setOpenSubMenu(null);
      if ((isMobile || isTablet) && !isHorizontal) setIsSidebarOpen(false);
      if ((isMobile || isTablet) && isHorizontal) setIsMobileMenuOpen(false);
    }
  };

  const toggleNestedSubMenu = (name) => {
    setOpenNestedSubMenu((prev) => (prev === name ? null : name));
  };

  const handleNestedLeafClick = (name) => {
    handleItemClick(name, false);
    setOpenNestedSubMenu(null);
  };

  const handleLogout = () => {
    clearSession();
    navigate("/login", { replace: true });
    toast.success("Logged out successfully");
  };

  const handleProfileClick = () => {
    navigate("/profile");
    setIsMobileMenuOpen(false);
  };

  const hideThemeToggle = isForcedLightPage;

  const isSmall = isMobile || isTablet;

  if (isHorizontal) {
    return (
      <div className="flex flex-col h-screen w-full bg-background text-foreground overflow-hidden">
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              key="mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden"
              style={{ zIndex: 1000 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>

        <aside
          className="bg-sidebar border-b border-sidebar-border shadow-sm w-full relative"
          style={{
            zIndex: isMobileMenuOpen || openSubMenu || isProfileOpen ? 1001 : 40,
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 h-14">
            <div className="flex items-center gap-2">
              <img
                src={mawgiflogo}
                alt="Logo"
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="text-lg font-bold text-sidebar-foreground tracking-tight hidden sm:block">
                Crystal Ball
              </span>
            </div>

            <nav
              ref={desktopNavRef}
              className="hidden lg:flex flex-1 justify-center px-6"
            >
              <ul className="flex space-x-1 items-center">
                {filteredNavItems.map((item, index) => {
                  const isActive = item.hasSubmenu
                    ? isSubmenuActive(item.submenu)
                    : location.pathname === item.path;

                  return (
                    <li key={index} className="relative">
                      {item.hasSubmenu ? (
                        <div className="relative group">
                          <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleItemClick(item.name, true)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                              isActive
                                ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold"
                                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            }`}
                          >
                            <item.icon size={18} />
                            <span className="text-sm">{item.name}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform duration-200 ${
                                openSubMenu === item.name ? "rotate-180" : ""
                              }`}
                            />
                          </motion.button>

                          <AnimatePresence>
                            {openSubMenu === item.name && (
                              <motion.ul
                                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                transition={{ duration: 0.15 }}
                                className="absolute top-full left-0 mt-1 bg-sidebar rounded-lg shadow-xl border border-sidebar-border p-1.5 z-[99999] min-w-[200px]"
                              >
                                {item.submenu.map((subItem, idx) => (
                                  <li key={idx}>
                                    {subItem.hasSubmenu ? (
                                      <div className="relative group/nested">
                                        <button
                                          onClick={() =>
                                            toggleNestedSubMenu(subItem.name)
                                          }
                                          className={`flex items-center justify-between w-full px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                                            isSubmenuActive(subItem.submenu)
                                              ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                              : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                          }`}
                                        >
                                          <span>{subItem.name}</span>
                                          <ChevronDown
                                            size={12}
                                            className={`ml-2 transform transition-transform duration-200 ${
                                              openNestedSubMenu === subItem.name
                                                ? "rotate-180"
                                                : ""
                                            }`}
                                          />
                                        </button>
                                        <Collapse
                                          isOpen={
                                            openNestedSubMenu === subItem.name
                                          }
                                        >
                                          <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-sidebar-border pl-2">
                                            {subItem.submenu.map(
                                              (nestedItem, nidx) => (
                                                <li key={nidx}>
                                                  <NavLink
                                                    to={nestedItem.path}
                                                    onClick={() =>
                                                      handleNestedLeafClick(
                                                        nestedItem.name
                                                      )
                                                    }
                                                    className={({
                                                      isActive:
                                                        isNestedActive,
                                                    }) =>
                                                      `block px-3 py-1.5 text-sm whitespace-nowrap rounded-md transition-colors ${
                                                        isNestedActive
                                                          ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                                          : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                                      }`
                                                    }
                                                  >
                                                    {nestedItem.name}
                                                  </NavLink>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </Collapse>
                                      </div>
                                    ) : (
                                      <NavLink
                                        to={subItem.path}
                                        onClick={() =>
                                          handleItemClick(subItem.name, false)
                                        }
                                        className={({
                                          isActive: isSubActive,
                                        }) =>
                                          `block px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                                            isSubActive
                                              ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                              : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                          }`
                                        }
                                      >
                                        {subItem.name}
                                      </NavLink>
                                    )}
                                  </li>
                                ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <NavLink
                          to={item.path}
                          onClick={() => handleItemClick(item.name, false)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                            isActive
                              ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          }`}
                        >
                          <item.icon size={18} />
                          <span className="text-sm">{item.name}</span>
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex items-center gap-2">
              <motion.button
                ref={hamburgerRef}
                whileTap={{ scale: 0.9 }}
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 text-sidebar-foreground/70 hover:bg-sidebar-accent rounded-lg transition-colors"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={isMobileMenuOpen ? "close" : "open"}
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="block"
                  >
                    {isMobileMenuOpen ? (
                      <X className="w-5 h-5" />
                    ) : (
                      <Menu className="w-5 h-5" />
                    )}
                  </motion.span>
                </AnimatePresence>
              </motion.button>

              <ProfileDropdown
                user={user}
                theme={theme}
                setTheme={setTheme}
                isHorizontal={true}
                onOpenChange={setIsProfileOpen}
                onProfileClick={handleProfileClick}
                onToggleLayout={toggleLayoutMode}
                onLogout={handleLogout}
                hideThemeToggle={hideThemeToggle}
              />
            </div>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                key="mobile-menu"
                ref={mobileMenuRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="lg:hidden absolute top-full left-0 right-0 border-t border-sidebar-border bg-sidebar shadow-xl max-h-[calc(100vh-3.5rem)] overflow-y-auto"
              >
                <nav className="p-3">
                  <ul className="space-y-1">
                    {filteredNavItems.map((item, index) => {
                      const isActive = item.hasSubmenu
                        ? isSubmenuActive(item.submenu)
                        : location.pathname === item.path;

                      return (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: 0.05 + index * 0.04,
                            duration: 0.25,
                          }}
                        >
                          {item.hasSubmenu ? (
                            <div>
                              <button
                                onClick={() =>
                                  handleItemClick(item.name, true)
                                }
                                className={`flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold"
                                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                }`}
                              >
                                <item.icon size={18} className="flex-shrink-0" />
                                <span className="ml-2.5 flex-1 text-left text-sm">
                                  {item.name}
                                </span>
                                <ChevronDown
                                  size={14}
                                  className={`transform transition-transform duration-200 ${
                                    openSubMenu === item.name
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                />
                              </button>
                              <Collapse isOpen={openSubMenu === item.name}>
                                <ul className="ml-5 mt-1 space-y-0.5">
                                  {item.submenu.map((subItem, idx) => (
                                    <li key={idx}>
                                      {subItem.hasSubmenu ? (
                                        <div>
                                          <button
                                            onClick={() =>
                                              toggleNestedSubMenu(subItem.name)
                                            }
                                            className={`flex items-center justify-between w-full px-3 py-2 text-xs rounded-md transition-colors ${
                                              isSubmenuActive(subItem.submenu)
                                                ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                                : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                            }`}
                                          >
                                            <span>{subItem.name}</span>
                                            <ChevronDown
                                              size={12}
                                              className={`transform transition-transform duration-200 ${
                                                openNestedSubMenu ===
                                                subItem.name
                                                  ? "rotate-180"
                                                  : ""
                                              }`}
                                            />
                                          </button>
                                          <Collapse
                                            isOpen={
                                              openNestedSubMenu === subItem.name
                                            }
                                          >
                                            <ul className="ml-4 mt-0.5 space-y-0.5">
                                              {subItem.submenu.map(
                                                (nestedItem, nidx) => (
                                                  <li key={nidx}>
                                                    <NavLink
                                                      to={nestedItem.path}
                                                      onClick={() =>
                                                        handleNestedLeafClick(
                                                          nestedItem.name
                                                        )
                                                      }
                                                      className={({
                                                        isActive:
                                                          isNestedActive,
                                                      }) =>
                                                        `block px-3 py-1.5 text-[11px] rounded-md transition-colors ${
                                                          isNestedActive
                                                            ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                                            : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                                        }`
                                                      }
                                                    >
                                                      {nestedItem.name}
                                                    </NavLink>
                                                  </li>
                                                )
                                              )}
                                            </ul>
                                          </Collapse>
                                        </div>
                                      ) : (
                                        <NavLink
                                          to={subItem.path}
                                          onClick={() =>
                                            handleItemClick(
                                              subItem.name,
                                              false
                                            )
                                          }
                                          className={({
                                            isActive: isSubActive,
                                          }) =>
                                            `block px-3 py-2 text-xs rounded-md transition-colors ${
                                              isSubActive
                                                ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                                : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                            }`
                                          }
                                        >
                                          {subItem.name}
                                        </NavLink>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </Collapse>
                            </div>
                          ) : (
                            <NavLink
                              to={item.path}
                              onClick={() => handleItemClick(item.name, false)}
                              className={`flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 ${
                                isActive
                                  ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                              }`}
                            >
                              <item.icon size={18} className="flex-shrink-0" />
                              <span className="ml-2.5 text-sm">
                                {item.name}
                              </span>
                            </NavLink>
                          )}
                        </motion.li>
                      );
                    })}
                  </ul>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </aside>

        {location.pathname === "/cube" ||
        thisPages.includes(location.pathname) ? (
          <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {children}
            {/* FloatingChatbot is now in ProtectedRoute — removed from here */}
          </main>
        ) : (
          <main
            className={
              location.pathname === "/board-pack"
                ? "flex-1 min-h-0 overflow-hidden bg-muted/10"
                : `flex-1 px-3 md:px-4 lg:px-5 pb-3 md:pb-4 lg:pb-5 ${
                    location.pathname === "/" ? "pt-0" : "pt-3 md:pt-4 lg:pt-5"
                  } ${
                    location.pathname === "/cube" ||
                    location.pathname === "/assign-permission" ||
                    location.pathname === "/" ||
                    location.pathname === "/financial-dashboard" ||
                    location.pathname === "/finalized-boardpacks" ||
                    location.pathname === "/mapping"
                      ? "bg-muted/10 overflow-y-auto"
                      : "dark:bg-muted/10 bg-[#F2F3F8]"
                  }`
            }
          >
            <div
              className={
                location.pathname === "/board-pack" ? "h-full min-h-0" : "mx-auto"
              }
            >
              {children}
              {/* FloatingChatbot removed from here too */}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full bg-background text-foreground overflow-hidden">
      {/* Overlay (mobile / tablet) */}
      <AnimatePresence initial={false}>
        {isSidebarOpen && isSmall && (
          <motion.div
            key="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 1000 }}
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence initial={false}>
        {isSidebarOpen && (
          <motion.aside
            key="sidebar"
            ref={sidebarRef}
            initial={
              isSmall
                ? { x: "-100%", width: 256 }
                : { x: 0, width: isCollapsed ? 80 : 256 }
            }
            animate={{ x: 0, width: isCollapsed ? 80 : 256 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            style={{ zIndex: isSmall ? 1001 : 40 }}
            className={`
              bg-sidebar border-r border-sidebar-border shadow-lg overflow-hidden
              flex flex-col h-full flex-shrink-0
              ${isSmall ? "fixed inset-y-0 left-0" : "relative"}
            `}
          >
            <div className="flex items-center justify-between px-4 border-b border-sidebar-border/50 flex-shrink-0 h-14">
              <div className="flex items-center gap-2.5 min-w-0">
                <img
                  src={mawgiflogo}
                  alt="Logo"
                  className="h-8 w-auto object-contain flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                {!isCollapsed && (
                  <span className="text-lg font-bold text-sidebar-foreground tracking-tight truncate">
                    Crystal Ball
                  </span>
                )}
              </div>
              {isSmall && (
                <motion.button
                  whileTap={{ scale: 0.9, rotate: 90 }}
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1.5 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              )}
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 scrollbar-hide">
              <ul className="space-y-0.5">
                {filteredNavItems.map((item, index) => {
                  const isActive = item.hasSubmenu
                    ? isSubmenuActive(item.submenu)
                    : location.pathname === item.path;

                  return (
                    <motion.li
                      key={index}
                      initial={isSmall ? { opacity: 0, x: -14 } : false}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + index * 0.05, duration: 0.28 }}
                    >
                      <HoverFlyout
                        enabled={isCollapsed && !isSmall}
                        item={item}
                        onNavigate={handleNestedLeafClick}
                      >
                        {item.hasSubmenu ? (
                          <div>
                            <button
                              onClick={() => handleItemClick(item.name, true)}
                              className={`flex items-center ${
                                isCollapsed ? "justify-center" : "justify-between"
                              } w-full px-3 py-2.5 rounded-lg transition-all duration-200 ${
                                isActive
                                  ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-sm"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                              }`}
                            >
                              <div
                                className={`flex items-center ${
                                  isCollapsed ? "" : "gap-2.5"
                                }`}
                              >
                                <item.icon size={18} className="flex-shrink-0" />
                                {!isCollapsed && (
                                  <span className="text-xs">{item.name}</span>
                                )}
                              </div>
                              {!isCollapsed && (
                                <ChevronDown
                                  size={14}
                                  className={`transform transition-transform duration-200 ${
                                    openSubMenu === item.name
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                />
                              )}
                            </button>
                            <Collapse
                              isOpen={openSubMenu === item.name && !isCollapsed}
                            >
                              <ul className="ml-5 mt-1 space-y-0.5">
                                {item.submenu.map((subItem, idx) => (
                                  <li key={idx}>
                                    {subItem.hasSubmenu ? (
                                      <div>
                                        <button
                                          onClick={() =>
                                            toggleNestedSubMenu(subItem.name)
                                          }
                                          className={`flex items-center justify-between w-full px-3 py-2 text-xs rounded-md transition-colors ${
                                            isSubmenuActive(subItem.submenu)
                                              ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                              : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                          }`}
                                        >
                                          <span className="truncate">
                                            {subItem.name}
                                          </span>
                                          <ChevronDown
                                            size={12}
                                            className={`transform transition-transform duration-200 ${
                                              openNestedSubMenu === subItem.name
                                                ? "rotate-180"
                                                : ""
                                            }`}
                                          />
                                        </button>
                                        <Collapse                                          isOpen={
                                            openNestedSubMenu === subItem.name
                                          }
                                        >
                                          <ul className="ml-4 mt-0.5 space-y-0.5">
                                            {subItem.submenu.map(
                                              (nestedItem, nidx) => (
                                                <li key={nidx}>
                                                  <NavLink
                                                    to={nestedItem.path}
                                                    onClick={() =>
                                                      handleNestedLeafClick(
                                                        nestedItem.name
                                                      )
                                                    }
                                                    className={({
                                                      isActive:
                                                        isNestedActive,
                                                    }) =>
                                                      `block px-3 py-1.5 text-[11px] rounded-md transition-colors truncate ${
                                                        isNestedActive
                                                          ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                                          : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                                      }`
                                                    }
                                                  >
                                                    {nestedItem.name}
                                                  </NavLink>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </Collapse>
                                      </div>
                                    ) : (
                                      <NavLink
                                        to={subItem.path}
                                        onClick={() =>
                                          handleItemClick(subItem.name, false)
                                        }
                                        className={({
                                          isActive: isSubActive,
                                        }) =>
                                          `block px-3 py-2 text-xs rounded-md transition-colors truncate ${
                                            isSubActive
                                              ? "text-sidebar-primary font-semibold bg-sidebar-accent/50"
                                              : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30"
                                          }`
                                        }
                                      >
                                        {subItem.name}
                                      </NavLink>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </Collapse>
                          </div>
                        ) : (
                          <NavLink
                            to={item.path}
                            onClick={() => handleItemClick(item.name, false)}
                            className={`flex items-center ${
                              isCollapsed ? "justify-center" : "gap-2.5"
                            } px-3 py-2.5 rounded-lg transition-all duration-200 ${
                              isActive
                                ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-sm"
                                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            }`}
                          >
                            <item.icon size={18} className="flex-shrink-0" />
                            {!isCollapsed && (
                              <span className="text-xs">{item.name}</span>
                            )}
                          </NavLink>
                        )}
                      </HoverFlyout>
                    </motion.li>
                  );
                })}
              </ul>
            </nav>
          </motion.aside>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="h-14 flex items-center justify-between px-4 lg:px-5 bg-background/95 backdrop-blur-md border-b border-border sticky top-0 flex-shrink-0"
          style={{ zIndex: isProfileOpen ? 1001 : 40 }}
        >
          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleSidebar}
              className="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
              aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              <Menu className="w-5 h-5" />
            </motion.button>
          </div>

          <div className="flex items-center gap-2">
            <ProfileDropdown
              user={user}
              theme={theme}
              setTheme={setTheme}
              isHorizontal={false}
              onOpenChange={setIsProfileOpen}
              onProfileClick={handleProfileClick}
              onToggleLayout={toggleLayoutMode}
              onLogout={handleLogout}
              hideThemeToggle={hideThemeToggle}
            />
          </div>
        </header>

        {thisPages.includes(location.pathname) ? (
          <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {children}
            {/* FloatingChatbot moved to ProtectedRoute */}
          </main>
        ) : (
          <main
            className={
              location.pathname === "/board-pack"
                ? "flex-1 min-h-0 overflow-hidden bg-muted/10"
                : `flex-1 px-3 md:px-4 lg:px-5 pb-3 md:pb-4 lg:pb-5 ${
                    location.pathname === "/" ? "pt-0" : "pt-3 md:pt-4 lg:pt-5"
                  } ${
                    location.pathname === "/cube" ||
                    location.pathname === "/assign-permission" ||
                    location.pathname === "/" ||
                    location.pathname === "/financial-dashboard" ||
                    location.pathname === "/finalized-boardpacks" ||
                    location.pathname === "/mapping"
                      ? "bg-muted/10 overflow-y-auto"
                      : "dark:bg-muted/10 bg-[#F2F3F8]"
                  }`
            }
          >
            <div
              className={
                location.pathname === "/board-pack" ? "h-full min-h-0" : "mx-auto"
              }
            >
              {children}
              {/* FloatingChatbot removed */}
            </div>
          </main>
        )}
      </div>
    </div>
  );
};

export default SidebarLayout;